import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, ShieldCheck } from "lucide-react";
import api, { inr, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useConfig } from "@/context/ConfigContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const config = useConfig();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("cod");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addr, setAddr] = useState({
    full_name: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "",
    email: user?.email || "",
  });

  const ship = subtotal >= config.free_ship_threshold ? 0 : config.ship_fee;
  const codFee = method === "cod" ? config.cod_fee : 0;
  const discount = applied?.discount || 0;
  const total = subtotal - discount + ship + codFee;

  useEffect(() => {
    if (items.length === 0 && !submitted) navigate("/cart");
  }, [items.length, submitted, navigate]);

  if (items.length === 0 && !submitted) return null;

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setApplied(data);
      toast.success(`Coupon applied — you saved ${inr(data.discount)}`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const validate = () => {
    const req = ["full_name", "phone", "line1", "city", "state", "pincode", "email"];
    for (const f of req) if (!addr[f]) { toast.error("Please complete your delivery details"); return false; }
    if (!/^\d{6}$/.test(addr.pincode)) { toast.error("Enter a valid 6-digit pincode"); return false; }
    if (!/^\d{10}$/.test(addr.phone)) { toast.error("Enter a valid 10-digit phone number"); return false; }
    return true;
  };

  const finalizeSuccess = (order) => {
    setSubmitted(true);
    const t = order.access_token ? `?t=${order.access_token}` : "";
    navigate(`/order-confirmation/${order.id}${t}`);
    clearCart();
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setProcessing(true);
    try {
      const payload = {
        items, payment_method: method, coupon_code: applied?.code || null,
        email: addr.email,
        address: { full_name: addr.full_name, phone: addr.phone, line1: addr.line1, line2: addr.line2, city: addr.city, state: addr.state, pincode: addr.pincode },
      };
      const { data: order } = await api.post("/orders", payload);

      if (method === "cod") {
        toast.success("Order placed successfully");
        finalizeSuccess(order);
        return;
      }

      // Razorpay
      if (order.demo_mode) {
        // MOCKED payment flow (demo keys)
        toast("Processing payment (demo mode)…");
        await api.post("/orders/razorpay/verify", {
          order_id: order.id,
          razorpay_payment_id: "pay_demo_" + Math.random().toString(36).slice(2, 12),
          razorpay_order_id: order.razorpay_order_id,
          razorpay_signature: "demo_signature",
        });
        toast.success("Payment successful (demo)");
        finalizeSuccess(order);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setProcessing(false); return; }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.total * 100,
        currency: "INR",
        name: "KKashigari India",
        order_id: order.razorpay_order_id,
        prefill: { name: addr.full_name, email: addr.email, contact: addr.phone },
        theme: { color: "#A08B5C" },
        handler: async (res) => {
          try {
            await api.post("/orders/razorpay/verify", {
              order_id: order.id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful");
            finalizeSuccess(order);
          } catch {
            toast.error("Payment verification failed");
            navigate(`/order-confirmation/${order.id}${order.access_token ? `?t=${order.access_token}` : ""}`);
          }
        },
        modal: { ondismiss: () => setProcessing(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setProcessing(false);
    }
  };

  const field = (name, label, extra = {}) => (
    <div>
      <label className="uplabel text-[0.62rem] block mb-1.5">{label}</label>
      <input value={addr[name]} onChange={(e) => setAddr({ ...addr, [name]: e.target.value })} data-testid={`addr-${name}`} {...extra}
        className="w-full bg-transparent border border-[var(--line)] px-3 py-2.5 outline-none text-[var(--bronze)] focus:border-[var(--olive)] transition-colors" />
    </div>
  );

  return (
    <div data-testid="checkout-page" className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 lg:py-16">
      <h1 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)] mb-12">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {field("full_name", "Full Name")}
              {field("phone", "Phone", { type: "tel", maxLength: 10, placeholder: "10-digit number" })}
              {field("email", "Email", { type: "email" })}
              {field("pincode", "Pincode", { maxLength: 6, placeholder: "6-digit pincode" })}
              <div className="sm:col-span-2">{field("line1", "Address Line 1")}</div>
              <div className="sm:col-span-2">{field("line2", "Address Line 2 (optional)")}</div>
              {field("city", "City")}
              {field("state", "State")}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Payment Method</h2>
            <div className="space-y-3">
              <label data-testid="pay-cod" className={`flex items-center gap-4 border p-5 cursor-pointer transition-colors ${method === "cod" ? "border-[var(--olive)] bg-[#F3EECB]" : "border-[var(--line)]"}`}>
                <input type="radio" name="pay" checked={method === "cod"} onChange={() => setMethod("cod")} className="accent-[var(--olive)]" />
                <div><div className="font-serif text-lg text-[var(--bronze)]">Cash on Delivery</div><div className="text-sm text-[var(--olive)]">Pay when you receive · ₹{config.cod_fee} handling fee</div></div>
              </label>
              <label data-testid="pay-razorpay" className={`flex items-center gap-4 border p-5 cursor-pointer transition-colors ${method === "razorpay" ? "border-[var(--olive)] bg-[#F3EECB]" : "border-[var(--line)]"}`}>
                <input type="radio" name="pay" checked={method === "razorpay"} onChange={() => setMethod("razorpay")} className="accent-[var(--olive)]" />
                <div><div className="font-serif text-lg text-[var(--bronze)]">Pay Online</div><div className="text-sm text-[var(--olive)]">Cards · UPI · Netbanking · Wallets {!config.razorpay_enabled && "(Demo Mode)"}</div></div>
              </label>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#F3EECB] p-8 sticky top-28">
            <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Order Summary</h2>
            <div className="space-y-3 max-h-48 overflow-y-auto hide-scrollbar mb-5">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.size}`} className="flex gap-3 text-sm">
                  <img src={it.image} alt="" className="w-12 aspect-[3/4] object-cover" />
                  <div className="flex-1"><div className="text-[var(--bronze)] leading-tight">{it.name}</div><div className="text-xs text-[var(--olive)]">Size {it.size} × {it.quantity}</div></div>
                  <span className="text-[var(--bronze)]">{inr(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-5">
              <div className="flex items-center border border-[var(--line)] flex-1 px-3">
                <Tag className="w-4 h-4 text-[var(--olive)]" />
                <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" data-testid="coupon-input"
                  className="bg-transparent px-2 py-2 text-sm flex-1 outline-none text-[var(--bronze)] uppercase placeholder:normal-case placeholder:text-[var(--olive)]/60" />
              </div>
              <button onClick={applyCoupon} data-testid="apply-coupon" className="uplabel text-[var(--bronze)] border border-[var(--line)] px-4 hover:bg-[var(--olive)] hover:text-[#FBF7DE] transition-colors">Apply</button>
            </div>

            <div className="space-y-2.5 text-sm border-t border-[var(--line)] pt-5">
              <div className="flex justify-between text-[var(--bronze)]"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[var(--emerald)]"><span>Discount ({applied.code})</span><span>-{inr(discount)}</span></div>}
              <div className="flex justify-between text-[var(--bronze)]"><span>Shipping</span><span>{ship === 0 ? "Free" : inr(ship)}</span></div>
              {codFee > 0 && <div className="flex justify-between text-[var(--bronze)]"><span>COD Fee</span><span>{inr(codFee)}</span></div>}
            </div>
            <div className="flex justify-between font-serif text-xl text-[var(--bronze)] mt-5 pt-5 border-t border-[var(--line)]">
              <span>Total</span><span data-testid="checkout-total">{inr(total)}</span>
            </div>

            <button onClick={placeOrder} disabled={processing} data-testid="place-order-btn" className="btn-primary w-full uplabel py-4 mt-6 disabled:opacity-60">
              {processing ? "Processing…" : method === "cod" ? "Place Order" : `Pay ${inr(total)}`}
            </button>
            <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-[var(--olive)]">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure & encrypted checkout
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
